import * as THREE from 'three';
import { NIGHT } from '../utils/constants';

export type NightSkyHandle = {
  group: THREE.Group;
  update: (dt: number) => void;
  setUnderpassBlend: (t: number) => void;
};

function createStarField(): THREE.Points {
  const count = NIGHT.STAR_COUNT;
  const positions = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);
  const sizes = new Float32Array(count);

  for (let i = 0; i < count; i++) {
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(Math.random() * 0.8 + 0.2);
    const r = 8000;

    positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
    positions[i * 3 + 1] = r * Math.cos(phi);
    positions[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta);

    const brightness = 0.5 + Math.random() * 0.5;
    const tint = Math.random();
    if (tint < 0.1) {
      colors[i * 3] = brightness;
      colors[i * 3 + 1] = brightness * 0.8;
      colors[i * 3 + 2] = brightness * 0.6;
    } else if (tint < 0.2) {
      colors[i * 3] = brightness * 0.7;
      colors[i * 3 + 1] = brightness * 0.8;
      colors[i * 3 + 2] = brightness;
    } else {
      colors[i * 3] = brightness;
      colors[i * 3 + 1] = brightness;
      colors[i * 3 + 2] = brightness;
    }

    sizes[i] = 1.5 + Math.random() * 3;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

  const material = new THREE.PointsMaterial({
    size: 2.5,
    vertexColors: true,
    transparent: true,
    opacity: 0.9,
    sizeAttenuation: false,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });

  return new THREE.Points(geometry, material);
}

function createMoon(): THREE.Mesh {
  const geometry = new THREE.SphereGeometry(150, 32, 32);
  const material = new THREE.MeshBasicMaterial({
    color: 0xf8f4e8,
    transparent: true,
    opacity: 0.95,
  });

  const moon = new THREE.Mesh(geometry, material);
  moon.position.set(3000, 2500, -4000);

  const glowGeometry = new THREE.SphereGeometry(180, 32, 32);
  const glowMaterial = new THREE.MeshBasicMaterial({
    color: 0xc0d0e8,
    transparent: true,
    opacity: 0.15,
    side: THREE.BackSide,
  });
  const glow = new THREE.Mesh(glowGeometry, glowMaterial);
  moon.add(glow);

  return moon;
}

function createSkyDome(): THREE.Mesh {
  const geometry = new THREE.SphereGeometry(9000, 32, 32);
  
  const vertexShader = `
    varying vec3 vWorldPosition;
    void main() {
      vec4 worldPosition = modelMatrix * vec4(position, 1.0);
      vWorldPosition = worldPosition.xyz;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `;

  const fragmentShader = `
    uniform vec3 topColor;
    uniform vec3 bottomColor;
    uniform vec3 horizonGlow;
    uniform float horizonHeight;
    uniform float glowStrength;
    varying vec3 vWorldPosition;

    void main() {
      float h = normalize(vWorldPosition).y;
      float t = max(0.0, h);
      
      vec3 sky = mix(bottomColor, topColor, pow(t, 0.4));
      
      float horizonFactor = 1.0 - abs(h - horizonHeight) * 8.0;
      horizonFactor = max(0.0, horizonFactor);
      sky = mix(sky, horizonGlow, horizonFactor * glowStrength);
      
      gl_FragColor = vec4(sky, 1.0);
    }
  `;

  const material = new THREE.ShaderMaterial({
    uniforms: {
      topColor: { value: new THREE.Color(0x000008) },
      bottomColor: { value: new THREE.Color(0x0a0818) },
      horizonGlow: { value: new THREE.Color(0x1a1028) },
      horizonHeight: { value: 0.0 },
      glowStrength: { value: 0.6 },
    },
    vertexShader,
    fragmentShader,
    side: THREE.BackSide,
    depthWrite: false,
  });

  return new THREE.Mesh(geometry, material);
}

export function createNightSky(): NightSkyHandle {
  const group = new THREE.Group();
  group.name = 'NightSky';

  const skyDome = createSkyDome();
  group.add(skyDome);

  const stars = createStarField();
  group.add(stars);

  const moon = createMoon();
  group.add(moon);

  let twinkleTime = 0;
  const starColors = stars.geometry.attributes.color.array as Float32Array;
  const baseColors = new Float32Array(starColors);

  const update = (dt: number) => {
    twinkleTime += dt;

    const colorAttr = stars.geometry.attributes.color;
    for (let i = 0; i < NIGHT.STAR_COUNT; i++) {
      const phase = twinkleTime * (2 + (i % 7) * 0.3) + i * 0.1;
      const twinkle = 0.7 + 0.3 * Math.sin(phase);
      starColors[i * 3] = baseColors[i * 3] * twinkle;
      starColors[i * 3 + 1] = baseColors[i * 3 + 1] * twinkle;
      starColors[i * 3 + 2] = baseColors[i * 3 + 2] * twinkle;
    }
    colorAttr.needsUpdate = true;
  };

  const setUnderpassBlend = (t: number) => {
    const mat = skyDome.material as THREE.ShaderMaterial;
    mat.uniforms['glowStrength'].value = 0.6 * (1 - t * 0.5);
    (stars.material as THREE.PointsMaterial).opacity = 0.9 * (1 - t * 0.8);
    (moon.material as THREE.MeshBasicMaterial).opacity = 0.95 * (1 - t * 0.7);
  };

  return { group, update, setUnderpassBlend };
}
